import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useCart } from '../context/CartContext';
import { Package, Minus, Plus, Trash2, ShoppingBag, ArrowRight, Tag } from 'lucide-react';

const DELIVERY_FEE = 1.000;
import { getImageUrl } from '../lib/api';

const CartPage = () => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, getItemPrice } = useCart();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f8fafc]">
        <Navbar />
        <div className="flex-1 flex items-center justify-center py-20" data-testid="empty-cart">
          <div className="text-center max-w-sm">
            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="w-12 h-12 text-slate-300" />
            </div>
            <h2
              className="text-2xl font-bold text-slate-800 mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Your cart is empty
            </h2>
            <p className="text-slate-500 mb-8">Add some products to get started</p>
            <Link to="/products">
              <button className="inline-flex items-center gap-2 bg-[#8acb95] hover:bg-[#7bb885] text-white font-semibold px-8 py-3.5 rounded-full transition-all shadow-sm hover:shadow-md">
                Browse Products
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Navbar />

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-10 flex-1">
        <h1
          className="text-3xl font-bold text-slate-800 mb-8"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Shopping Cart
          <span className="ml-3 text-base font-medium text-slate-400">
            ({cart.length} {cart.length === 1 ? 'item' : 'items'})
          </span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {cart.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex gap-4"
                data-testid={`cart-item-${item.id}`}
              >
                {/* Image */}
                <div className="w-28 h-28 bg-slate-50 rounded-xl overflow-hidden flex-shrink-0 border border-slate-100">
                  {item.images && item.images.length > 0 ? (
                    <img
                      src={getImageUrl(item.images[0])}
                      alt={item.name}
                      className="w-full h-full object-contain p-2"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-8 h-8 text-slate-200" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <p className="text-[10px] text-slate-400 uppercase tracking-widest font-medium">
                        {item.brand}
                      </p>
                      <h3
                        className="font-semibold text-slate-800 text-sm leading-snug line-clamp-2"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        {item.name}
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">SKU: {item.code}</p>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      data-testid={`remove-${item.id}`}
                      className="flex-shrink-0 p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
                    {/* Price */}
                    <div>
                      {item.discount_rate > 0 ? (
                        <>
                          <span className="text-xs text-slate-400 line-through block">
                            KWD {item.price.toFixed(3)}
                          </span>
                          <span className="text-base font-bold text-red-600">
                            KWD {getItemPrice(item).toFixed(3)}
                          </span>
                          <span className="ml-1.5 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium">
                            -{item.discount_rate}%
                          </span>
                        </>
                      ) : (
                        <span className="text-base font-bold text-[#8acb95]">
                          KWD {item.price.toFixed(3)}
                        </span>
                      )}
                    </div>

                    {/* Quantity */}
                    <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        data-testid={`decrease-${item.id}`}
                        className="px-3 py-2 hover:bg-slate-50 transition-colors text-slate-600"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-10 text-center text-sm font-bold text-slate-800">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        data-testid={`increase-${item.id}`}
                        className="px-3 py-2 hover:bg-slate-50 transition-colors text-slate-600"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 sticky top-28" data-testid="order-summary">
              <h2
                className="text-lg font-bold text-slate-800 mb-5"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Order Summary
              </h2>

              <div className="space-y-3 mb-5">
                {cart.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm gap-2">
                    <span className="text-slate-500 truncate flex-1">
                      {item.name} × {item.quantity}
                    </span>
                    <span className="font-medium text-slate-700 flex-shrink-0">
                      KWD {(getItemPrice(item) * item.quantity).toFixed(3)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-100 pt-4 mb-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-medium">KWD {getCartTotal().toFixed(3)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Delivery Fee</span>
                  <span className="font-medium text-slate-700">KWD {DELIVERY_FEE.toFixed(3)}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                  <span className="font-semibold text-slate-800">Total</span>
                  <span className="text-xl font-bold text-[#8acb95]">
                    KWD {(getCartTotal() + DELIVERY_FEE).toFixed(3)}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Payment: Cash on Delivery
                </p>
              </div>

              <Link to="/checkout">
                <button
                  className="w-full flex items-center justify-center gap-2 bg-[#8acb95] hover:bg-[#7bb885] text-white font-bold py-4 rounded-2xl transition-all shadow-sm hover:shadow-md text-sm"
                  data-testid="proceed-checkout"
                >
                  Proceed to Checkout
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>

              <Link to="/products">
                <button className="w-full mt-3 py-3 text-sm text-slate-500 hover:text-[#8acb95] transition-colors font-medium">
                  ← Continue Shopping
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
